import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import type {
  Pageable,
  CompanyFilterSpec,
  CompanyDTO,
  CompanyAddressDTO,
  CompanyContactDTO,
  CompanySignImageDTO,
  CreateCompanyRequest,
  CreateCompanyAddressRequest,
  CreateCompanyContactRequest,
  UpdateCompanyConfigRequest,
  UploadCompanySignImageRequest,
  WrapperResponseListCompanyDTO,
  WrapperResponseCompanyDTO,
  WrapperResponseCompanyAddressDTO,
  WrapperResponseCompanyContactDTO,
  WrapperResponseCompanySignImageDTO
} from '../core/models';

export type {
  CompanyDTO,
  CompanyAddressDTO,
  CompanyContactDTO,
  CompanySignImageDTO,
  CreateCompanyRequest,
  CreateCompanyAddressRequest,
  CreateCompanyContactRequest,
  UpdateCompanyConfigRequest,
  UploadCompanySignImageRequest,
  WrapperResponseListCompanyDTO,
  WrapperResponseCompanyDTO,
  WrapperResponseCompanyAddressDTO,
  WrapperResponseCompanyContactDTO,
  WrapperResponseCompanySignImageDTO
} from '../core/models';

@Injectable({
  providedIn: 'root'
})
export class CompanyService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  searchCompanies(
    spec: CompanyFilterSpec = {},
    pageable: Pageable = { page: 0, size: 20 }
  ): Observable<WrapperResponseListCompanyDTO> {
    let params = new HttpParams();
    Object.entries(spec).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.append(key, String(value));
      }
    });
    params = params.append('page', String(pageable.page));
    params = params.append('size', String(pageable.size));
    if (pageable.sort?.length) {
      pageable.sort.forEach(sort => params = params.append('sort', sort));
    }
    return this.http.get<WrapperResponseListCompanyDTO>(`${this.apiUrl}/companies`, { params });
  }

  getCompanyById(companyId: number): Observable<WrapperResponseCompanyDTO> {
    return this.http.get<WrapperResponseCompanyDTO>(`${this.apiUrl}/companies/${companyId}`);
  }

  /**
   * Obtiene la empresa del usuario autenticado (sin necesidad de companyId en el token).
   */
  getMyCompany(): Observable<WrapperResponseCompanyDTO> {
    return this.http.get<WrapperResponseCompanyDTO>(`${this.apiUrl}/companies/me`);
  }

  createCompany(request: CreateCompanyRequest): Observable<WrapperResponseCompanyDTO> {
    return this.http.post<WrapperResponseCompanyDTO>(`${this.apiUrl}/companies`, request);
  }

  updateCompany(companyId: number, request: CreateCompanyRequest): Observable<WrapperResponseCompanyDTO> {
    return this.http.put<WrapperResponseCompanyDTO>(`${this.apiUrl}/companies/${companyId}`, request);
  }

  updateCompanyConfig(
    companyId: number,
    request: UpdateCompanyConfigRequest
  ): Observable<WrapperResponseCompanyDTO> {
    return this.http.post<WrapperResponseCompanyDTO>(`${this.apiUrl}/companies/${companyId}/config`, request);
  }

  createCompanyAddress(companyId: number, request: CreateCompanyAddressRequest): Observable<WrapperResponseCompanyAddressDTO> {
    return this.http.post<WrapperResponseCompanyAddressDTO>(`${this.apiUrl}/companies/${companyId}/addresses`, request);
  }

  updateCompanyAddress(
    companyId: number,
    addressId: number,
    request: CreateCompanyAddressRequest
  ): Observable<WrapperResponseCompanyAddressDTO> {
    return this.http.put<WrapperResponseCompanyAddressDTO>(
      `${this.apiUrl}/companies/${companyId}/addresses/${addressId}`,
      request
    );
  }

  deleteCompanyAddress(companyId: number, addressId: number): Observable<{ ok: boolean; code: number; message: string; body: null }> {
    return this.http.delete<{ ok: boolean; code: number; message: string; body: null }>(
      `${this.apiUrl}/companies/${companyId}/addresses/${addressId}`
    );
  }

  createCompanyContact(companyId: number, request: CreateCompanyContactRequest): Observable<WrapperResponseCompanyContactDTO> {
    return this.http.post<WrapperResponseCompanyContactDTO>(`${this.apiUrl}/companies/${companyId}/contacts`, request);
  }

  updateCompanyContact(
    companyId: number,
    contactId: number,
    request: CreateCompanyContactRequest
  ): Observable<WrapperResponseCompanyContactDTO> {
    return this.http.put<WrapperResponseCompanyContactDTO>(
      `${this.apiUrl}/companies/${companyId}/contacts/${contactId}`,
      request
    );
  }

  deleteCompanyContact(companyId: number, contactId: number): Observable<{ ok: boolean; code: number; message: string; body: null }> {
    return this.http.delete<{ ok: boolean; code: number; message: string; body: null }>(
      `${this.apiUrl}/companies/${companyId}/contacts/${contactId}`
    );
  }

  uploadSignImage(
    companyId: number,
    request: UploadCompanySignImageRequest
  ): Observable<WrapperResponseCompanySignImageDTO> {
    return this.http.post<WrapperResponseCompanySignImageDTO>(
      `${this.apiUrl}/companies/${companyId}/sign-images`,
      request
    );
  }
}
